/**
 * Shared stats dashboard – same UI for drops and lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { BarChart3, Download, GitCompare, RefreshCw } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { LoadingPanel } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import {
  formatDateDMY,
  formatDateTimeDMYHM,
} from '@/features/smswiser-campaigns/utils/dateFormat';

import { StatsTable, StatsCompareTable, type StatsTableFullDatasetAggregate } from './table';
import { StatsCompareChart } from './chart/StatsCompareChart';
import {
  DateRangePresetDropdown,
  LimitSelect,
  FilterBarWithPresets,
  GroupBySelect,
} from './filter';
import ComparePresetBar from '@/features/dashboard/compare/components/controls/ComparePresetBar';
import GranularitySegment from '@/features/dashboard/compare/components/controls/GranularitySegment';
import {
  matchComparePreset,
  type ComparePresetCode,
} from '@/features/dashboard/compare/state/comparePresets';
import type { Granularity } from '@/features/dashboard/compare/data/types';
import { VOLUUM_COLUMNS } from './shared/constants';
import type { GroupByKey, ColumnDef } from './shared/constants';
import {
  CsvExportModal,
  buildCsvColumnsForTab,
  type ServerCsvExportSortConfig,
} from './csv-export';
import type { ServerCsvExportOptions } from './csv-export/hooks/useCsvExportModalState.types';
import { aggregateVoluum, extractStatsErrorMessage, toRangeISO } from './shared';
import type { GenericStatsRow } from './shared/generic-stats.types';
import type { FiltersState, FilterConfig } from './shared';
import type { StatsGroupGeneric } from './hooks';

interface FilterField {
  key: string;
  label: string;
  type: 'text' | 'numeric';
}

interface GroupByOption {
  key: string;
  label: string;
}

interface CampaignStatsDashboardProps {
  tab: string;
  title: string;
  emptyMessage: string;
  loadingTitle: string;
  loadingDesc: string;
  errorPrefix: string;
  firstColumnLabel: string;
  /** Sort key for first column (e.g. "dropList" for drops, "list" for lists). Default: dropList */
  firstColumnSortKey?: string;
  rowUnitLabel?: string;
  filterFields: FilterField[];
  groupByOptions: readonly GroupByOption[];
  rows: GenericStatsRow[];
  totals: Record<string, number>;
  groups: StatsGroupGeneric[] | null;
  totalRowCount: number;
  /** Optional custom label for totals row (e.g. "Total (5 drops) (23 texts)") */
  totalLabel?: string;
  /** Tooltip for the "(X drops)" part (e.g. texts tab). */
  totalLabelDropsTooltip?: string;
  allRowsLength: number;
  currency: string;
  fromDate: string;
  toDate: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  /** When provided, used for preset changes — updates both dates atomically. */
  onDateRangeChange?: (from: string, to: string) => void;
  length: number;
  onLengthChange: (v: number) => void;
  filtersState: FiltersState;
  setFilters: (updater: (p: FiltersState) => FiltersState) => void;
  defaultFilter: FilterConfig;
  groupBy: string;
  setGroupBy: (v: string) => void;
  collapsedGroups: Set<string>;
  setCollapsedGroups: (updater: (prev: Set<string>) => Set<string>) => void;
  toggleGroup: (key: string) => void;
  sort: { field: string; dir: 'asc' | 'desc' } | null;
  onSortClick: (field: string) => void;
  selectedRows: GenericStatsRow[];
  setSelectedRows: (updater: (prev: GenericStatsRow[]) => GenericStatsRow[]) => void;
  renderFirstCell: (row: GenericStatsRow) => ReactNode;
  isError: boolean;
  error: unknown;
  isLoading: boolean;
  /** When true, shows spinner on Refresh. Pass query.isFetching for refetch feedback. */
  isFetching?: boolean;
  dataUpdatedAt?: number;
  refetch: () => void;
  /** Optional slot for action buttons (Scale, Copy as draft when drops) */
  actionButtons?: ReactNode;
  showLimitSelect?: boolean;
  /** When true, LimitSelect includes "All" option (Phone tab). */
  allowAllLimit?: boolean;
  /** Show group-by selector (drops only; lists/gateways/sources have no grouping). Default: true */
  showGroupBySelect?: boolean;
  /** Optional responsive min-width for first column (e.g. path/lander/offer: "min-w-[280px] md:min-w-[480px]") */
  firstColumnClassName?: string;
  /** When filtering by tags (Lists tab), options for the dropdown. If provided, shows a tag select. */
  tagFilterOptions?: string[];
  /** Tag name -> hex color for styling tag filter options. */
  tagFilterColors?: Record<string, string>;
  /** When tags are loading, show LoadingSpinner in filter bar. */
  tagsLoading?: boolean;
  /** When filtering by staticTag (Drops tab), options for the dropdown. If provided, shows select. */
  staticTagFilterOptions?: string[];
  /** Static tag name -> hex color. */
  staticTagFilterColors?: Record<string, string>;
  /** When static tags are loading, show LoadingSpinner. */
  staticTagsLoading?: boolean;
  /** When set, shows Download CSV button. */
  csvExportUserId?: string;
  /** Phone tab + “All”: full server CSV (Voluum bulk fetch + server filters). */
  serverCsvExport?: (
    columnKeys: string[],
    sortOverride?: { field: string; dir: 'asc' | 'desc' } | null,
    options?: ServerCsvExportOptions,
  ) => Promise<void>;
  /** When set with server CSV, modal shows sort field / direction (e.g. Phone “All”). */
  serverCsvSortConfig?: ServerCsvExportSortConfig;
  /** Voluum total row count for server CSV (phone “All”) — loading overlay time hint. */
  serverCsvExportEstimatedRowCount?: number | null;
  csvExportContext?: {
    smswiserCountryId?: number | null;
    countryKey?: string | null;
  };
  /** Phone limit “All”: full Voluum aggregate row in table (totals only). */
  fullDatasetAggregate?: StatsTableFullDatasetAggregate;
  /** Phone “All”: bar filters differ from applied full-dataset query (prompt Apply). */
  fullDatasetFiltersStale?: boolean;
  /** Extra controls after the filter bar (e.g. Apply full-dataset filters). */
  filterToolbarExtra?: ReactNode;
  /** Work date for client CSV row-order (drops work-day split; optional elsewhere). */
  csvExportWorkDate?: string;
  /** With server CSV: drive “exclude cross-source noted rows” from parent (phone full-dataset aggregate). */
  controlledExcludeCrossSourceNotedRows?: boolean;
  onControlledExcludeCrossSourceNotedRowsChange?: (value: boolean) => void;
  /** Compare mode (per-row Range A | Range B | Δ). Only the flat dimensional tabs pass these. */
  compareCapable?: boolean;
  compareMode?: boolean;
  onToggleCompare?: () => void;
  compareFromDate?: string;
  compareToDate?: string;
  onCompareFromChange?: (v: string) => void;
  onCompareToChange?: (v: string) => void;
  onCompareRangeChange?: (from: string, to: string) => void;
  /** Range B metrics keyed by row id, + Range B totals (per-metric). */
  compareById?: Map<string, Record<string, number> | null>;
  compareTotals?: Record<string, number>;
  /** Dual-range preset ("Last week → This week", …) — sets both A (main) and B atomically. */
  onComparePreset?: (code: ComparePresetCode) => void;
  granularity?: Granularity;
  onGranularityChange?: (g: Granularity) => void;
  /** For the country trend chart (compare mode). */
  countryKey?: string;
  wiserIds?: number[];
}

export const CampaignStatsDashboard: FC<CampaignStatsDashboardProps> = ({
  tab,
  title,
  emptyMessage,
  loadingTitle,
  loadingDesc,
  errorPrefix,
  firstColumnLabel,
  firstColumnSortKey = 'dropList',
  rowUnitLabel = 'row',
  filterFields,
  groupByOptions,
  rows,
  totals,
  groups,
  totalRowCount,
  totalLabel,
  totalLabelDropsTooltip,
  allRowsLength,
  currency,
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onDateRangeChange,
  length,
  onLengthChange,
  filtersState,
  setFilters,
  defaultFilter,
  groupBy,
  setGroupBy,
  collapsedGroups,
  setCollapsedGroups,
  toggleGroup,
  sort,
  onSortClick,
  selectedRows,
  setSelectedRows,
  renderFirstCell,
  isError,
  error,
  isLoading,
  isFetching,
  dataUpdatedAt,
  refetch,
  actionButtons,
  showLimitSelect = true,
  allowAllLimit = false,
  showGroupBySelect = true,
  firstColumnClassName,
  tagFilterOptions,
  tagFilterColors,
  tagsLoading,
  staticTagFilterOptions,
  staticTagFilterColors,
  staticTagsLoading,
  csvExportUserId,
  serverCsvExport,
  serverCsvSortConfig,
  serverCsvExportEstimatedRowCount,
  csvExportContext,
  fullDatasetAggregate,
  fullDatasetFiltersStale,
  filterToolbarExtra,
  csvExportWorkDate,
  controlledExcludeCrossSourceNotedRows,
  onControlledExcludeCrossSourceNotedRowsChange,
  compareCapable,
  compareMode,
  onToggleCompare,
  compareFromDate = '',
  compareToDate = '',
  onCompareFromChange,
  onCompareToChange,
  onCompareRangeChange,
  compareById,
  compareTotals,
  onComparePreset,
  granularity,
  onGranularityChange,
  countryKey,
  wiserIds,
}) => {
  const { hasPermission } = useAuth();
  const canManageCsvTemplates = hasPermission('stats.csv_export_templates.manage');
  const range = toRangeISO(fromDate, toDate);
  const compareActive = !!compareCapable && !!compareMode;
  const noop = () => undefined;
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const csvColumns = buildCsvColumnsForTab(
    tab,
    firstColumnSortKey,
    firstColumnLabel,
    csvExportContext,
  );

  const showTableDespiteNoRows =
    !!fullDatasetAggregate &&
    (fullDatasetAggregate.loading ||
      fullDatasetAggregate.error ||
      typeof fullDatasetAggregate.rowCount === 'number');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <BarChart3 className="h-4 w-4 text-primary" />
            {title}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span>
              Range: {formatDateDMY(range.from)} → {formatDateDMY(range.to)}
            </span>
            {dataUpdatedAt ? (
              <span>Last reload: {formatDateTimeDMYHM(new Date(dataUpdatedAt))}</span>
            ) : null}
            {tagsLoading && (
              <span className="inline-flex items-center gap-1.5 text-primary/70">
                <LoadingSpinner className="h-3 w-3" />
                Loading tags…
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1.5">
            {compareActive && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/15 px-1 text-[11px] font-bold text-primary">
                A
              </span>
            )}
            <DateRangePresetDropdown
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={onFromChange}
              onToChange={onToChange}
              onDateRangeChange={onDateRangeChange}
            />
          </div>
          {compareCapable && (
            <Button
              variant={compareMode ? 'default' : 'outline'}
              size="sm"
              onClick={onToggleCompare}
              title="Compare two date ranges per row"
            >
              <GitCompare className="mr-2 h-4 w-4" />
              Compare
            </Button>
          )}
          {showLimitSelect && (
            <LimitSelect value={length} onChange={onLengthChange} allowAll={allowAllLimit} />
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading || isFetching}>
            <RefreshCw className={cn('mr-2 h-4 w-4', (isFetching ?? isLoading) && 'animate-spin')} />
            Refresh
          </Button>
          {csvExportUserId && rows.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCsvModalOpen(true)}
            >
              <Download className="mr-2 h-4 w-4" />
              Download CSV
            </Button>
          )}
          {actionButtons}
        </div>
      </div>

      {compareActive && (
        <div className="flex flex-col gap-2 rounded-lg border border-primary/20 bg-primary/[0.03] p-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <ComparePresetBar
              active={matchComparePreset(
                { from: fromDate, to: toDate },
                { from: compareFromDate, to: compareToDate },
              )}
              onSelect={onComparePreset ?? (() => undefined)}
            />
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Chart
              </span>
              <GranularitySegment
                value={granularity ?? 'day'}
                onChange={onGranularityChange ?? (() => undefined)}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">vs</span>
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/15 px-1 text-[11px] font-bold text-primary">
                B
              </span>
              <DateRangePresetDropdown
                fromDate={compareFromDate}
                toDate={compareToDate}
                onFromChange={onCompareFromChange ?? noop}
                onToChange={onCompareToChange ?? noop}
                onDateRangeChange={onCompareRangeChange}
              />
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">A</span> (current) ={' '}
            {formatDateDMY(range.from)} → {formatDateDMY(range.to)} ·{' '}
            <span className="font-semibold text-foreground">B</span> (baseline) ={' '}
            {formatDateDMY(compareFromDate)} → {formatDateDMY(compareToDate)} · Δ = A − B
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <FilterBarWithPresets
          tab={tab}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={onDateRangeChange}
          filtersState={filtersState}
          setFilters={setFilters}
          defaultFilter={defaultFilter}
          rowsLength={rows.length}
          allRowsLength={allRowsLength}
          filterFields={filterFields}
          tagFilterOptions={tagFilterOptions}
          tagFilterColors={tagFilterColors}
          tagsLoading={tagsLoading}
          staticTagFilterOptions={staticTagFilterOptions}
          staticTagFilterColors={staticTagFilterColors}
          staticTagsLoading={staticTagsLoading}
        />
        {filterToolbarExtra}
        {showGroupBySelect && (
          <GroupBySelect
            value={groupBy as GroupByKey}
            onChange={(v) => setGroupBy(v)}
            groups={groups}
            collapsedGroups={collapsedGroups}
            onCollapseAll={() => {
              if (!groups) return;
              const allKeys = new Set<string>();
              for (const g of groups) {
                allKeys.add(g.key);
                if (g.subGroups) for (const sg of g.subGroups) allKeys.add(sg.key);
              }
              setCollapsedGroups(() => allKeys);
            }}
            onExpandAll={() => {
              if (!groups) return;
              const hasNested = groups.some((g) => g.subGroups && g.subGroups.length > 0);
              if (hasNested) {
                setCollapsedGroups((prev) => {
                  const next = new Set(prev);
                  for (const g of groups) next.delete(g.key);
                  return next;
                });
              } else {
                setCollapsedGroups(() => new Set());
              }
            }}
            groupByOptions={groupByOptions}
          />
        )}
        {rows.length > 0 && (
          <>
            {selectedRows.length < rows.length && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedRows(() => [...rows])}
              >
                Select all
              </Button>
            )}
            {selectedRows.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setSelectedRows(() => [])}
              >
                Deselect all
              </Button>
            )}
          </>
        )}
      </div>

      {compareActive && countryKey && (wiserIds?.length ?? 0) > 0 ? (
        <StatsCompareChart
          countryKey={countryKey}
          wiserIds={wiserIds ?? []}
          rangeA={{ from: fromDate, to: toDate }}
          rangeB={{ from: compareFromDate, to: compareToDate }}
          granularity={granularity ?? 'day'}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
        <div className="min-h-0 flex-1 overflow-auto">
          {isError ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-8">
              <p className="text-sm text-destructive text-center max-w-md">
                {errorPrefix} {extractStatsErrorMessage(error)}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : isLoading ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <LoadingPanel
                size="lg"
                title={loadingTitle}
                description={loadingDesc}
                className="flex min-h-0 flex-1 flex-col items-center justify-center border-primary/30 bg-primary/5"
              />
            </div>
          ) : rows.length === 0 && !showTableDespiteNoRows ? (
            <div className="px-4 py-8 text-sm text-muted-foreground">{emptyMessage}</div>
          ) : compareActive ? (
            <StatsCompareTable
              rows={rows}
              compareById={compareById ?? new Map()}
              totalsA={totals}
              totalsB={compareTotals ?? {}}
              columnDefs={VOLUUM_COLUMNS as ColumnDef[]}
              currency={currency}
              labelA={`${formatDateDMY(range.from)} → ${formatDateDMY(range.to)}`}
              labelB={`${formatDateDMY(compareFromDate)} → ${formatDateDMY(compareToDate)}`}
              firstColumnLabel={firstColumnLabel}
              renderFirstCell={renderFirstCell}
              firstColumnClassName={firstColumnClassName}
            />
          ) : (
            <StatsTable
              rows={rows}
              totals={totals}
              filteredTotals={
                rows.length < totalRowCount && rows.length > 0
                  ? aggregateVoluum(rows)
                  : undefined
              }
              filteredRowCount={
                rows.length < totalRowCount && rows.length > 0 ? rows.length : undefined
              }
              fullDatasetAggregate={fullDatasetAggregate}
              fullDatasetFiltersStale={fullDatasetFiltersStale}
              groups={groups}
              collapsedGroups={collapsedGroups}
              onToggleGroup={toggleGroup}
              totalRowCount={totalRowCount}
              totalLabel={totalLabel}
              totalLabelDropsTooltip={totalLabelDropsTooltip}
              currency={currency}
              columnDefs={VOLUUM_COLUMNS as ColumnDef[]}
              sort={sort}
              onSortClick={onSortClick}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              renderFirstCell={renderFirstCell}
              firstColumnLabel={firstColumnLabel}
              firstColumnSortKey={firstColumnSortKey}
              rowUnitLabel={rowUnitLabel}
              firstColumnClassName={firstColumnClassName}
            />
          )}
        </div>
      </div>
      {csvExportUserId && (
        <CsvExportModal
          open={csvModalOpen}
          onOpenChange={setCsvModalOpen}
          tab={tab}
          columns={csvColumns}
          rows={rows}
          userId={csvExportUserId}
          canManageTemplates={canManageCsvTemplates}
          serverCsvExport={serverCsvExport}
          serverCsvSortConfig={serverCsvSortConfig}
          serverExportEstimatedRowCount={serverCsvExportEstimatedRowCount}
          csvExportWorkDate={csvExportWorkDate}
          controlledExcludeCrossSourceNotedRows={controlledExcludeCrossSourceNotedRows}
          onControlledExcludeCrossSourceNotedRowsChange={
            onControlledExcludeCrossSourceNotedRowsChange
          }
        />
      )}
    </div>
  );
};
